import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Upload, 
  Download, 
  UserCheck, 
  UserX, 
  Plus,
  Search,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
  LogOut,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Calendar,
  TrendingUp,
  BarChart3,
  Settings,
  Bell,
  Sun,
  Moon,
  ChevronDown,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Award,
  UserPlus,
  FileText,
  Database,
  Shield,
  Menu,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../hooks/useTheme';
import { useNotifications } from './NotificationProvider';
import AdminSidebar from './AdminSidebar';

interface Student {
  _id: string;
  dni: string;
  nombre: string;
  apellido: string;
  email?: string;
  telefono?: string;
  isAuthorized: boolean;
  hasCompletedTest: boolean;
  nivelObtenido?: string;
  puntuacionTotal?: number;
  testModel?: string;
  createdAt: string;
}

const AdminDashboard: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedTestData, setSelectedTestData] = useState<any>(null);
  const [adminUser, setAdminUser] = useState<any>(null);
  const [selectedFilters, setSelectedFilters] = useState({
    status: 'all',
    testStatus: 'all',
    level: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const { addNotification } = useNotifications();

  // Formulario para agregar estudiante
  const [newStudent, setNewStudent] = useState({
    dni: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: ''
  });

  useEffect(() => {
    fetchStudents();
    // Obtener datos del administrador
    const userData = localStorage.getItem('user');
    if (userData) {
      setAdminUser(JSON.parse(userData));
    }
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/students', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students);
      addNotification('Estudiantes cargados exitosamente', 'success');
    } catch (error) {
      console.error('Error fetching students:', error);
      addNotification('Error al cargar estudiantes', 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleAuthorize = async (dni: string, isAuthorized: boolean) => {
    try {
      // El interceptor de axios ya agrega el token automáticamente
      const response = await axios.patch(
        `/students/${dni}/authorize`,
        { isAuthorized: !isAuthorized }
      );
      
      if (response.data.success) {
        addNotification(`Estudiante ${!isAuthorized ? 'autorizado' : 'desautorizado'} exitosamente`, 'success');
        fetchStudents();
      } else {
        addNotification(response.data.message || 'Error al cambiar autorización', 'error');
      }
    } catch (error: any) {
      console.error('Error authorizing student:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al cambiar autorización del estudiante';
      addNotification(errorMessage, 'error');
      
      // Si el error es de autenticación, redirigir al login
      if (error.response?.status === 401 || error.response?.status === 403) {
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      }
    }
  };

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/students',
        newStudent,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewStudent({ dni: '', nombre: '', apellido: '', email: '', telefono: '' });
      setShowAddModal(false);
      addNotification('Estudiante creado exitosamente', 'success');
      fetchStudents();
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Error creando estudiante', 'error');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingFile(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'http://localhost:5000/api/students/upload-excel',
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      addNotification(`✅ ${response.data.results.created} creados, ${response.data.results.updated} actualizados`, 'success');
      fetchStudents();
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Error subiendo archivo', 'error');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://testchildren.ibcidiomas.com/api/students/export/excel', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'estudiantes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      addNotification('Archivo exportado exitosamente', 'success');
    } catch (error) {
      console.error('Error exporting:', error);
      addNotification('Error al exportar archivo', 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const handleViewTest = async (studentId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://testchildren.ibcidiomas.com/api/placement/admin/student-test/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedTestData(response.data);
      setShowTestModal(true);
      addNotification('Test cargado exitosamente', 'success');
    } catch (error: any) {
      console.error('Error fetching test data:', error);
      addNotification('Error al cargar el test del estudiante', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Estadísticas calculadas
  const stats = useMemo(() => {
    const total = students.length;
    const authorized = students.filter(s => s.isAuthorized).length;
    const completed = students.filter(s => s.hasCompletedTest).length;
    const levels = students.reduce((acc, s) => {
      if (s.nivelObtenido) {
        acc[s.nivelObtenido] = (acc[s.nivelObtenido] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return { total, authorized, completed, levels };
  }, [students]);

  // Filtrado y ordenamiento
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students.filter(s => {
      const matchesSearch = s.dni.includes(searchTerm) ||
    s.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.apellido.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = selectedFilters.status === 'all' || 
        (selectedFilters.status === 'authorized' && s.isAuthorized) ||
        (selectedFilters.status === 'unauthorized' && !s.isAuthorized);
      
      const matchesTestStatus = selectedFilters.testStatus === 'all' ||
        (selectedFilters.testStatus === 'completed' && s.hasCompletedTest) ||
        (selectedFilters.testStatus === 'pending' && !s.hasCompletedTest);
      
      const matchesLevel = selectedFilters.level === 'all' ||
        s.nivelObtenido === selectedFilters.level;

      return matchesSearch && matchesStatus && matchesTestStatus && matchesLevel;
    });

    // Ordenamiento
    filtered.sort((a, b) => {
      let aValue = a[sortBy as keyof Student];
      let bValue = b[sortBy as keyof Student];
      
      // Manejar valores undefined/null
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';
      
      // Convertir a string para comparación
      const aStr = typeof aValue === 'string' ? aValue.toLowerCase() : String(aValue).toLowerCase();
      const bStr = typeof bValue === 'string' ? bValue.toLowerCase() : String(bValue).toLowerCase();
      
      if (sortOrder === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    return filtered;
  }, [students, searchTerm, selectedFilters, sortBy, sortOrder]);

  // Paginación
  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedStudents.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedStudents, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedStudents.length / itemsPerPage);

  return (
    <div style={{
      minHeight: '100vh',
      transition: 'background-color 0.3s ease',
      backgroundColor: darkMode ? '#111827' : '#f9fafb'
    }}>
      {/* Sidebar */}
      <AdminSidebar 
        isOpen={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)} 
        darkMode={darkMode} 
      />

      {/* Header Principal */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: darkMode ? '#1f2937' : 'white',
          borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginLeft: '18rem'
        }}
      >
        <div style={{
          maxWidth: '80rem',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '1rem',
            paddingBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                  color: darkMode ? '#d1d5db' : '#4b5563',
                  border: 'none',
                  cursor: 'pointer',
                  display: window.innerWidth >= 1024 ? 'none' : 'block',
                  marginRight: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                }}
              >
                <Menu size={20} />
              </button>
              
          <div>
                <h1 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>
                  Gestión de Estudiantes
            </h1>
                <p style={{
                  fontSize: '0.875rem',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  margin: 0
                }}>
                  Administra y supervisa el progreso de los estudiantes
                </p>
          </div>
          </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              {/* Información del Administrador */}
              {adminUser && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                  borderRadius: '0.75rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <div style={{
                    width: '2rem',
                    height: '2rem',
                    backgroundColor: darkMode ? '#1f2937' : '#e5e7eb',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: darkMode ? '#d1d5db' : '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}>
                    {adminUser.nombre ? adminUser.nombre.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: darkMode ? 'white' : '#111827'
                    }}>
                      {adminUser.nombre || 'Administrador'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: darkMode ? '#9ca3af' : '#6b7280'
                    }}>
                      Administrador
                    </div>
                  </div>
                </div>
              )}
              
          <button
                onClick={toggleDarkMode}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                  color: darkMode ? '#fbbf24' : '#4b5563',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                }}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
          <button
                onClick={fetchStudents}
                disabled={loading}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                  color: darkMode ? '#d1d5db' : '#4b5563',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                  }
                }}
              >
                <RefreshCw style={{
                  animation: loading ? 'spin 1s linear infinite' : 'none'
                }} size={20} />
          </button>
        </div>
      </div>
        </div>
      </motion.div>

      <div style={{
        maxWidth: '80rem',
        margin: '0 auto',
        padding: '1rem 1rem',
        marginLeft: '18rem'
      }}>
        {/* Estadísticas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1rem'
          }}
        >
          <div style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#dbeafe',
                borderRadius: '0.5rem'
              }}>
                <Users style={{ color: '#2563eb' }} size={24} />
              </div>
              <div style={{ marginLeft: '1rem' }}>
                <p style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  margin: 0
                }}>Total Estudiantes</p>
                <p style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>{stats.total}</p>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#dcfce7',
                borderRadius: '0.5rem'
              }}>
                <UserCheck style={{ color: '#16a34a' }} size={24} />
              </div>
              <div style={{ marginLeft: '1rem' }}>
                <p style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  margin: 0
                }}>Autorizados</p>
                <p style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>{stats.authorized}</p>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#f3e8ff',
                borderRadius: '0.5rem'
              }}>
                <CheckCircle style={{ color: '#9333ea' }} size={24} />
              </div>
              <div style={{ marginLeft: '1rem' }}>
                <p style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  margin: 0
                }}>Exámenes Completados</p>
                <p style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>{stats.completed}</p>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fed7aa',
                borderRadius: '0.5rem'
              }}>
                <TrendingUp style={{ color: '#ea580c' }} size={24} />
              </div>
              <div style={{ marginLeft: '1rem' }}>
                <p style={{
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  margin: 0
                }}>Tasa de Completado</p>
                <p style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>
                  {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Barra de Acciones */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            padding: '1rem',
            marginBottom: '1rem',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth >= 1024 ? 'row' : 'column',
            alignItems: window.innerWidth >= 1024 ? 'center' : 'stretch',
            justifyContent: window.innerWidth >= 1024 ? 'space-between' : 'flex-start',
            gap: '1rem'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
          <button
            onClick={() => setShowAddModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10b981';
                }}
              >
                <UserPlus style={{ marginRight: '0.5rem' }} size={20} />
            Agregar Estudiante
          </button>
          
              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
              }}>
                <Upload style={{ marginRight: '0.5rem' }} size={20} />
            {uploadingFile ? 'Subiendo...' : 'Cargar Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
                  style={{ display: 'none' }}
              disabled={uploadingFile}
            />
          </label>

          <button
            onClick={handleExport}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#8b5cf6';
                }}
              >
                <Download style={{ marginRight: '0.5rem' }} size={20} />
            Exportar Excel
          </button>

          <a
            href="/students_template.xlsx"
            download
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6b7280';
                }}
              >
                <FileText style={{ marginRight: '0.5rem' }} size={20} />
            Plantilla Excel
          </a>
        </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: showFilters ? '#3b82f6' : (darkMode ? '#374151' : '#f3f4f6'),
                  color: showFilters ? 'white' : (darkMode ? '#d1d5db' : '#4b5563'),
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                  }
                }}
              >
                <Filter style={{ marginRight: '0.5rem' }} size={20} />
                Filtros
              </button>
      </div>
          </div>
        </motion.div>

        {/* Filtros Avanzados */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                backgroundColor: darkMode ? '#1f2937' : 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                overflow: 'hidden'
              }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Estado de Autorización
                  </label>
                  <select
                    value={selectedFilters.status}
                    onChange={(e) => setSelectedFilters({...selectedFilters, status: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                      borderRadius: '0.5rem',
                      outline: 'none',
                      backgroundColor: darkMode ? '#374151' : 'white',
                      color: darkMode ? 'white' : '#111827',
                      fontSize: '0.875rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all">Todos</option>
                    <option value="authorized">Autorizados</option>
                    <option value="unauthorized">No Autorizados</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Estado del Examen
                  </label>
                  <select
                    value={selectedFilters.testStatus}
                    onChange={(e) => setSelectedFilters({...selectedFilters, testStatus: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                      borderRadius: '0.5rem',
                      outline: 'none',
                      backgroundColor: darkMode ? '#374151' : 'white',
                      color: darkMode ? 'white' : '#111827',
                      fontSize: '0.875rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all">Todos</option>
                    <option value="completed">Completados</option>
                    <option value="pending">Pendientes</option>
                  </select>
      </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Nivel Obtenido
                  </label>
                  <select
                    value={selectedFilters.level}
                    onChange={(e) => setSelectedFilters({...selectedFilters, level: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                      borderRadius: '0.5rem',
                      outline: 'none',
                      backgroundColor: darkMode ? '#374151' : 'white',
                      color: darkMode ? 'white' : '#111827',
                      fontSize: '0.875rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="all">Todos</option>
                    <option value="A1">A1</option>
                    <option value="A2">A2</option>
                    <option value="B1">B1</option>
                    <option value="B2">B2</option>
                    <option value="C1">C1</option>
                    <option value="C2">C2</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Barra de Búsqueda y Ordenamiento */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth >= 1024 ? 'row' : 'column',
            alignItems: window.innerWidth >= 1024 ? 'center' : 'stretch',
            gap: '1rem'
          }}>
            <div style={{
              flex: 1,
              position: 'relative'
            }}>
              <Search style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} size={20} />
          <input
            type="text"
            placeholder="Buscar por DNI, nombre o apellido..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '2.5rem',
                  paddingRight: '1rem',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
          />
        </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '0.875rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="createdAt">Fecha de Registro</option>
                <option value="nombre">Nombre</option>
                <option value="apellido">Apellido</option>
                <option value="dni">DNI</option>
                <option value="nivelObtenido">Nivel</option>
              </select>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                  color: darkMode ? '#d1d5db' : '#4b5563',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                }}
              >
                <ChevronDown style={{
                  transform: `rotate(${sortOrder === 'asc' ? '180' : '0'}deg)`,
                  transition: 'transform 0.2s ease'
                }} size={20} />
              </button>
      </div>
          </div>
        </motion.div>

        {/* Tabla de Estudiantes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            backgroundColor: darkMode ? '#1f2937' : 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}
        >
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem 0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <RefreshCw style={{
                  animation: 'spin 1s linear infinite',
                  color: '#3b82f6'
                }} size={24} />
                <span style={{
                  fontSize: '1.125rem',
                  color: darkMode ? '#d1d5db' : '#6b7280'
                }}>
                  Cargando estudiantes...
                </span>
              </div>
            </div>
          ) : (
            <>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
                  <thead style={{
                    backgroundColor: darkMode ? '#374151' : '#f9fafb'
                  }}>
                    <tr>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Estudiante
                      </th>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Estado
                      </th>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Examen
                      </th>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Nivel
                      </th>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Fecha
                      </th>
                      <th style={{
                        padding: '1rem 1.5rem',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: darkMode ? '#d1d5db' : '#374151'
                      }}>
                        Acciones
                      </th>
              </tr>
            </thead>
                  <tbody style={{
                    borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
                  }}>
                    {paginatedStudents.map((student, index) => (
                      <motion.tr
                        key={student._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        style={{
                          borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              width: '2.5rem',
                              height: '2.5rem',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              backgroundColor: student.isAuthorized ? '#10b981' : '#9ca3af'
                            }}>
                              {student.nombre.charAt(0)}{student.apellido.charAt(0)}
                            </div>
                            <div style={{ marginLeft: '1rem' }}>
                              <div style={{
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: darkMode ? 'white' : '#111827'
                              }}>
                                {student.nombre} {student.apellido}
                              </div>
                              <div style={{
                                fontSize: '0.875rem',
                                color: darkMode ? '#9ca3af' : '#6b7280'
                              }}>
                                DNI: {student.dni}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap'
                        }}>
                    {student.isAuthorized ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: '#dcfce7',
                        color: '#166534'
                      }}>
                        <CheckCircle style={{ marginRight: '0.25rem' }} size={14} />
                        Autorizado
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: '#fef2f2',
                        color: '#991b1b'
                      }}>
                        <XCircle style={{ marginRight: '0.25rem' }} size={14} />
                        No Autorizado
                      </span>
                    )}
                  </td>
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap'
                        }}>
                    {student.hasCompletedTest ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: '#dbeafe',
                                color: '#1e40af'
                              }}>
                                <CheckCircle2 style={{ marginRight: '0.25rem' }} size={14} />
                                Completado
                              </span>
                              {student.testModel && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  backgroundColor: '#f3e8ff',
                                  color: '#6b21a8',
                                  marginTop: '0.25rem'
                                }}>
                                  <FileText style={{ marginRight: '0.25rem' }} size={14} />
                                  {student.testModel}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: '#fef3c7',
                              color: '#92400e'
                            }}>
                              <Clock style={{ marginRight: '0.25rem' }} size={14} />
                              Pendiente
                            </span>
                    )}
                  </td>
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {student.nivelObtenido ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: '#f3e8ff',
                              color: '#6b21a8'
                            }}>
                              <Award style={{ marginRight: '0.25rem' }} size={14} />
                              {student.nivelObtenido}
                            </span>
                          ) : (
                            <span style={{
                              fontSize: '0.875rem',
                              color: darkMode ? '#9ca3af' : '#6b7280'
                            }}>
                              -
                            </span>
                    )}
                  </td>
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                          color: darkMode ? '#d1d5db' : '#6b7280'
                        }}>
                          {new Date(student.createdAt).toLocaleDateString('es-ES')}
                  </td>
                        <td style={{
                          padding: '1rem 1.5rem',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleAuthorize(student.dni, student.isAuthorized)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        backgroundColor: student.isAuthorized ? '#fef2f2' : '#f0fdf4',
                        color: student.isAuthorized ? '#991b1b' : '#166534',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = student.isAuthorized ? '#fee2e2' : '#dcfce7';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = student.isAuthorized ? '#fef2f2' : '#f0fdf4';
                      }}
                    >
                      {student.isAuthorized ? (
                        <><UserX style={{ marginRight: '0.25rem' }} size={14} /> Desautorizar</>
                      ) : (
                        <><UserCheck style={{ marginRight: '0.25rem' }} size={14} /> Autorizar</>
                      )}
                    </button>
                    
                    {student.hasCompletedTest && (
                      <button
                        onClick={() => handleViewTest(student._id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#bfdbfe';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#dbeafe';
                        }}
                      >
                        <Eye style={{ marginRight: '0.25rem' }} size={14} /> Ver Test
                      </button>
                    )}
                          </div>
                  </td>
                      </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  backgroundColor: darkMode ? '#1f2937' : '#f9fafb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: darkMode ? '#9ca3af' : '#374151'
                    }}>
                      Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredAndSortedStudents.length)} de {filteredAndSortedStudents.length} resultados
      </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          transition: 'all 0.2s ease',
                          backgroundColor: currentPage === 1 ? '#e5e7eb' : (darkMode ? '#374151' : 'white'),
                          color: currentPage === 1 ? '#9ca3af' : (darkMode ? '#d1d5db' : '#374151'),
                          border: 'none',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1) {
                            e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== 1) {
                            e.currentTarget.style.backgroundColor = darkMode ? '#374151' : 'white';
                          }
                        }}
                      >
                        Anterior
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              backgroundColor: currentPage === page ? '#3b82f6' : (darkMode ? '#374151' : 'white'),
                              color: currentPage === page ? 'white' : (darkMode ? '#d1d5db' : '#374151'),
                              border: 'none',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              if (currentPage !== page) {
                                e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (currentPage !== page) {
                                e.currentTarget.style.backgroundColor = darkMode ? '#374151' : 'white';
                              }
                            }}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          transition: 'all 0.2s ease',
                          backgroundColor: currentPage === totalPages ? '#e5e7eb' : (darkMode ? '#374151' : 'white'),
                          color: currentPage === totalPages ? '#9ca3af' : (darkMode ? '#d1d5db' : '#374151'),
                          border: 'none',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== totalPages) {
                            e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== totalPages) {
                            e.currentTarget.style.backgroundColor = darkMode ? '#374151' : 'white';
                          }
                        }}
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Modal para Agregar Estudiante */}
      <AnimatePresence>
      {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              zIndex: 50
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              style={{
                backgroundColor: darkMode ? '#1f2937' : 'white',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                maxWidth: '28rem',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: 0
                }}>
                  Agregar Estudiante
                </h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    color: darkMode ? '#9ca3af' : '#6b7280',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
            <form onSubmit={handleAddStudent} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    DNI *
                  </label>
              <input
                type="text"
                    placeholder="Ingrese el DNI"
                value={newStudent.dni}
                onChange={(e) => setNewStudent({ ...newStudent, dni: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Nombre *
                  </label>
              <input
                type="text"
                    placeholder="Ingrese el nombre"
                value={newStudent.nombre}
                onChange={(e) => setNewStudent({ ...newStudent, nombre: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Apellido *
                  </label>
              <input
                type="text"
                    placeholder="Ingrese el apellido"
                value={newStudent.apellido}
                onChange={(e) => setNewStudent({ ...newStudent, apellido: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Email
                  </label>
              <input
                type="email"
                    placeholder="Ingrese el email (opcional)"
                value={newStudent.email}
                onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}>
                    Teléfono
                  </label>
              <input
                type="text"
                    placeholder="Ingrese el teléfono (opcional)"
                value={newStudent.telefono}
                onChange={(e) => setNewStudent({ ...newStudent, telefono: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '0.5rem',
                  outline: 'none',
                  backgroundColor: darkMode ? '#374151' : 'white',
                  color: darkMode ? 'white' : '#111827',
                  fontSize: '1rem'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                  />
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  paddingTop: '1rem'
                }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                  }}
                >
                    <UserPlus style={{ marginRight: '0.5rem' }} size={20} />
                    Crear Estudiante
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    backgroundColor: darkMode ? '#374151' : '#e5e7eb',
                    color: darkMode ? '#d1d5db' : '#374151'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#e5e7eb';
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
          </motion.div>
      )}
      </AnimatePresence>

      {/* Modal para Ver Test del Estudiante */}
      <AnimatePresence>
        {showTestModal && selectedTestData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              zIndex: 50
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              style={{
                backgroundColor: darkMode ? '#1f2937' : 'white',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                maxWidth: '80rem',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
              }}
            >
              {/* Header del Modal */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem',
                borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                paddingBottom: '1rem'
              }}>
                <div>
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: darkMode ? 'white' : '#111827',
                    margin: 0
                  }}>
                    Test Completado por {selectedTestData.student?.nombre} {selectedTestData.student?.apellido}
                  </h2>
                  <p style={{
                    fontSize: '0.875rem',
                    color: darkMode ? '#9ca3af' : '#6b7280',
                    margin: '0.25rem 0 0 0'
                  }}>
                    DNI: {selectedTestData.student?.dni} | Nivel: {selectedTestData.testData?.assignedLevel || 'No determinado'}
                  </p>
        </div>
                <button
                  onClick={() => setShowTestModal(false)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    color: darkMode ? '#9ca3af' : '#6b7280',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Información del Test */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  backgroundColor: darkMode ? '#374151' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: darkMode ? '#d1d5db' : '#374151',
                    margin: '0 0 0.5rem 0'
                  }}>Puntuación Total</h3>
                  <p style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: darkMode ? 'white' : '#111827',
                    margin: 0
                  }}>{selectedTestData.testData?.totalScore || 0} puntos</p>
                </div>
                
                <div style={{
                  backgroundColor: darkMode ? '#374151' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: darkMode ? '#d1d5db' : '#374151',
                    margin: '0 0 0.5rem 0'
                  }}>Preguntas Correctas</h3>
                  <p style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: darkMode ? 'white' : '#111827',
                    margin: 0
                  }}>{selectedTestData.statistics?.correctAnswers || 0} de {selectedTestData.statistics?.totalQuestions || 0}</p>
                </div>
                
                <div style={{
                  backgroundColor: darkMode ? '#374151' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: darkMode ? '#d1d5db' : '#374151',
                    margin: '0 0 0.5rem 0'
                  }}>Porcentaje</h3>
                  <p style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: darkMode ? 'white' : '#111827',
                    margin: 0
                  }}>{selectedTestData.statistics?.percentage || 0}%</p>
                </div>
                
                <div style={{
                  backgroundColor: darkMode ? '#374151' : '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                }}>
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: darkMode ? '#d1d5db' : '#374151',
                    margin: '0 0 0.5rem 0'
                  }}>Fecha de Completado</h3>
                  <p style={{
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: darkMode ? 'white' : '#111827',
                    margin: 0
                  }}>{selectedTestData.testData?.completedAt ? new Date(selectedTestData.testData.completedAt).toLocaleDateString('es-ES') : 'No disponible'}</p>
                </div>
              </div>

              {/* Preguntas y Respuestas */}
              <div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: darkMode ? 'white' : '#111827',
                  margin: '0 0 1rem 0'
                }}>Preguntas y Respuestas</h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem'
                }}>
                  {selectedTestData.questions?.map((question: any, index: number) => (
                    <div key={index} style={{
                      backgroundColor: darkMode ? '#374151' : '#f9fafb',
                      padding: '1.5rem',
                      borderRadius: '0.75rem',
                      border: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{
                          backgroundColor: darkMode ? '#1f2937' : 'white',
                          color: darkMode ? 'white' : '#111827',
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: darkMode ? 'white' : '#111827',
                            margin: '0 0 0.75rem 0'
                          }}>{question.question}</h4>
                          
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem'
                          }}>
                            {question.options?.map((option: any, optionIndex: number) => (
                              <div key={optionIndex} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem',
                                borderRadius: '0.5rem',
                                backgroundColor: option.isCorrect 
                                  ? '#dcfce7' 
                                  : option.isSelected 
                                    ? '#fef2f2' 
                                    : darkMode ? '#4b5563' : '#e5e7eb',
                                border: `2px solid ${
                                  option.isCorrect 
                                    ? '#16a34a' 
                                    : option.isSelected 
                                      ? '#ef4444' 
                                      : 'transparent'
                                }`
                              }}>
                                <div style={{
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  borderRadius: '50%',
                                  backgroundColor: option.isCorrect 
                                    ? '#16a34a' 
                                    : option.isSelected 
                                      ? '#ef4444' 
                                      : '#9ca3af',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}>
                                  {String.fromCharCode(65 + optionIndex)}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <span style={{
                                    color: option.isCorrect 
                                      ? '#166534' 
                                      : option.isSelected 
                                        ? '#991b1b' 
                                        : darkMode ? '#d1d5db' : '#374151',
                                    fontWeight: option.isSelected ? '600' : '400',
                                    display: 'block',
                                    marginBottom: '0.25rem'
                                  }}>{option.text}</span>
                                  
                                  {option.isCorrect && (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      color: '#16a34a',
                                      fontWeight: '600',
                                      display: 'block'
                                    }}>
                                      ✅ Respuesta correcta
                                    </span>
                                  )}
                                  
                                  {option.isSelected && !option.isCorrect && (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      color: '#ef4444',
                                      fontWeight: '600',
                                      display: 'block'
                                    }}>
                                      ❌ Respuesta seleccionada por el estudiante
                                    </span>
                                  )}
                                </div>
                                
                                {option.isCorrect && (
                                  <CheckCircle2 style={{ color: '#16a34a', marginLeft: 'auto' }} size={20} />
                                )}
                                {option.isSelected && !option.isCorrect && (
                                  <XCircle style={{ color: '#ef4444', marginLeft: 'auto' }} size={20} />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
